import { ChevronRight } from "lucide-react";
import { useState } from "react";

import { useMarkSplitExempt, useSetFollowUp, useUnlinkFollowUp, type QuestionDetail } from "@/api/queries";
import { useCan } from "@/auth/member";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Callout, ErrorNote } from "@/components/ui/panel";
import { cn } from "@/lib/utils";

/**
 * DESIGN §17.11.6 — a question's authored follow-up, shown the way the cluster is: a card for the
 * other end of the link that opens in this same pane, the reverse link when this question *is*
 * someone's follow-up, what the rewrite changed, and — for a reviewer — the way to change it.
 *
 * It says plainly when the link will not do what it promises: a follow-up that is not approved is
 * never served, and the picker silently falls back to derived follow-ups, which is exactly the kind
 * of thing nobody notices unless the screen says so.
 */
export function FollowUpSection({
  q,
  onOpenQuestion,
}: {
  q: QuestionDetail;
  onOpenQuestion?: ((id: number) => void) | undefined;
}) {
  const canEdit = useCan("review");
  const setFollowUp = useSetFollowUp();
  const unlink = useUnlinkFollowUp();
  const exempt = useMarkSplitExempt();
  const [choosing, setChoosing] = useState(false);
  const [target, setTarget] = useState("");

  const link = q.linked_follow_up ?? null;
  const followers = q.follow_up_of ?? [];
  const split = q.split ?? null;
  const exemptable = canEdit && split?.status !== "applied" && split?.status !== "exempt";
  if (!link && followers.length === 0 && !split && !canEdit) return null;

  const error = setFollowUp.error ?? unlink.error ?? exempt.error;
  const targetId = Number(target);

  function submit() {
    if (!Number.isInteger(targetId) || targetId < 1) return;
    setFollowUp.mutate({ id: q.id, followUpId: targetId }, { onSuccess: () => setChoosing(false) });
  }

  return (
    <div className="border-bank-border space-y-2.5 border-t pt-3">
      <p className="text-bank-muted font-mono text-[10.5px] tracking-[0.07em] uppercase">Follow-up</p>

      {link && (
        <div className="space-y-1.5">
          <LinkedCard
            id={link.id}
            status={link.status}
            text={link.question_text}
            label="asked right after this question"
            onOpen={onOpenQuestion}
          />
          {link.canonical_id !== null && (
            <Callout>
              #{link.id} was folded into a cluster; the picker reads through to its canonical, #
              {link.canonical_id}.
            </Callout>
          )}
          {link.status !== "approved" && link.canonical_id === null && (
            <Callout>
              The follow-up is <strong>{link.status}</strong>, not approved, so it is never served — the
              picker uses a derived follow-up for this question until it is.
            </Callout>
          )}
        </div>
      )}

      {followers.length > 0 && (
        <p className="text-sm">
          <span className="text-bank-muted">This is the authored follow-up for </span>
          {followers.map((id, i) => (
            <span key={id}>
              {i > 0 && ", "}
              {onOpenQuestion ? (
                <button
                  type="button"
                  onClick={() => onOpenQuestion(id)}
                  className="text-bank-accent-ink focus-visible:ring-bank-accent font-mono text-[13px] underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                >
                  #{id}
                </button>
              ) : (
                <span className="font-mono text-[13px]">#{id}</span>
              )}
            </span>
          ))}
          .
        </p>
      )}

      {split && (
        <details className="text-sm">
          <summary className="text-bank-muted cursor-pointer font-mono text-[11px]">
            original wording — {split.origin}, {split.status}
            {split.seconds_before !== null && split.seconds_primary !== null
              ? `, ~${split.seconds_before.toFixed(1)}s → ~${split.seconds_primary.toFixed(1)}s`
              : ""}
          </summary>
          <p className="question-text border-bank-border bg-bank-surface-2 mt-2 rounded-md border px-2.5 py-2 text-[13px]">
            {split.original_text}
          </p>
          {split.findings.length > 0 && (
            <ul className="text-bank-attention-ink mt-2 list-disc space-y-0.5 pl-4 text-xs">
              {split.findings.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
        </details>
      )}

      <ErrorNote error={error} />

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          {choosing ? (
            <>
              <Input
                type="number"
                min={1}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
                placeholder="follow-up question #"
                aria-label="Id of the question to use as the follow-up"
                className="w-44"
              />
              <Button
                size="sm"
                variant="primary"
                disabled={setFollowUp.isPending || !target}
                onClick={submit}
              >
                {setFollowUp.isPending ? "Linking…" : "Link"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setChoosing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setChoosing(true)}>
              {link ? "Change follow-up" : "Link a follow-up"}
            </Button>
          )}
          {link && !choosing && (
            <Button
              size="sm"
              variant="danger"
              disabled={unlink.isPending}
              onClick={() => unlink.mutate(q.id)}
            >
              {unlink.isPending ? "Unlinking…" : "Unlink"}
            </Button>
          )}
          {exemptable && !choosing && (
            <Button
              size="sm"
              variant="ghost"
              disabled={exempt.isPending}
              title="Long on purpose — never offered for splitting again"
              onClick={() => exempt.mutate(q.id)}
            >
              {exempt.isPending ? "Marking…" : "Mark long on purpose"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function LinkedCard({
  id,
  status,
  text,
  label,
  onOpen,
}: {
  id: number;
  status: string;
  text: string;
  label: string;
  onOpen?: ((id: number) => void) | undefined;
}) {
  const clickable = Boolean(onOpen);
  return (
    <div
      onClick={clickable ? () => onOpen!(id) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen!(id);
              }
            }
          : undefined
      }
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open question ${id}` : undefined}
      className={cn(
        "border-bank-border bg-bank-accent-wash/40 grid grid-cols-[1fr_auto] items-center gap-2 rounded-md border px-2.5 py-2",
        clickable &&
          "hover:bg-bank-accent-wash focus-visible:outline-bank-accent cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2",
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-bank-muted font-mono text-[11px] tabular-nums">#{id}</span>
          <Badge value={status} />
          <span className="text-bank-muted font-mono text-[10px] tracking-[0.05em] uppercase">{label}</span>
        </div>
        <p className="question-text mt-1 line-clamp-3 text-[13px]">{text}</p>
      </div>
      {clickable && <ChevronRight className="text-bank-muted size-4 shrink-0" aria-hidden="true" />}
    </div>
  );
}
